import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { useRole } from "@/lib/roleContext";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Save, ShieldCheck, Ticket, Mic2, Building2, Megaphone, Disc3, ArrowRight } from "lucide-react";
import { useEffect } from "react";
import { endpoints as _e } from "@/lib/api";
import SelfDeleteAccountSection from "@/components/SelfDeleteAccountSection";

// Human-readable label for an artist_members role from GET /api/auth/artists.
const ARTIST_ROLE_LABELS = {
    owner: "Artist Owner",
    manager: "Artist Manager",
    team: "Artist Team",
    staff: "Event Staff",
    admin: "Platform Admin",
};

const VENUE_ROLE_LABELS = {
    manager: "Venue Manager",
    staff: "Venue Staff",
    admin: "Platform Admin",
};

const PROVIDER_LABEL = {
    password: "via password",
    google: "via Google",
    emergent_google: "via Google",
};

export default function AccountSettings() {
    const { user, artists, venues, setUserAndArtists, activateFanPassport, logout } = useAuth();
    const { setActiveArtist } = useRole();
    const nav = useNavigate();
    const [name, setName] = useState(user?.name || "");
    const [currentPassword, setCurrent] = useState("");
    const [newPassword, setNew] = useState("");
    const [busy, setBusy] = useState(false);
    const [activating, setActivating] = useState(false);
    const [error, setError] = useState("");

    const saveProfile = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const d = await endpoints.authAccountUpdate({ name });
            await setUserAndArtists(d.user);
            toast.success("Profile updated");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await endpoints.authAccountUpdate({ current_password: currentPassword, new_password: newPassword });
            setCurrent(""); setNew("");
            toast.success("Password updated");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const doLogout = async () => {
        await logout();
        nav("/", { replace: true });
    };

    const openArtistWorkspace = (a) => {
        setActiveArtist(a.id, a.name);
        nav("/workspace");
    };
    const openVenueWorkspace = (v) => {
        nav(`/venue/${v.id}/overview`);
    };
    const openFanPassport = () => nav("/fan");

    const providerLabel = PROVIDER_LABEL[user?.provider] || `via ${user?.provider}`;
    const artistList = Array.isArray(artists) ? artists : [];
    const venueList = Array.isArray(venues) ? venues : [];
    const [promoterList, setPromoterList] = useState([]);
    const [labelList, setLabelList] = useState([]);
    useEffect(() => {
        (async () => {
            try { const r = await _e.authMyPromoters(); setPromoterList(r.promoters || []); } catch { setPromoterList([]); }
            try { const r = await _e.authMyLabels(); setLabelList(r.labels || []); } catch { setLabelList([]); }
        })();
    }, [user?.id]);

    return (
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-10" data-testid="account-settings">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Account</div>
            <h1 className="mt-1 font-display font-black uppercase tracking-tighter text-4xl">Settings</h1>

            <section className="mt-8 border border-[#2a2a2a] p-6">
                <div className="text-[10px] font-mono uppercase text-[#666]">Signed in as</div>
                <div className="mt-1 font-display font-bold uppercase text-xl" data-testid="account-email">{user?.email}</div>
                <div className="text-xs font-mono uppercase text-[#a3a3a3] mt-1 flex items-center gap-2" data-testid="account-identity-line">
                    <ShieldCheck className="w-3 h-3" /> Project Mint account · {providerLabel}
                </div>

                <div className="mt-5 border-t border-[#2a2a2a] pt-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-3">Your relationships on Project Mint</div>
                    <ul className="space-y-2" data-testid="account-relationships">
                        {(() => {
                            const hasFanPassport = !!user?.has_fan_passport;
                            const doActivate = async () => {
                                if (activating) return;
                                setActivating(true);
                                setError("");
                                try {
                                    await activateFanPassport();
                                    toast.success("Fan Passport activated");
                                    nav("/fan");
                                } catch (err) {
                                    setError(formatApiError(err));
                                } finally {
                                    setActivating(false);
                                }
                            };
                            return hasFanPassport ? (
                                <li
                                    role="button"
                                    tabIndex={0}
                                    onClick={openFanPassport}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openFanPassport(); }}
                                    className="group flex items-center gap-3 border border-[#2a2a2a] hover:border-[#ff4400] hover:bg-[#141414] p-3 cursor-pointer transition-colors"
                                    data-testid="account-relationship-fan"
                                >
                                    <Ticket className="w-4 h-4 text-[#ff4400] shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-display font-bold uppercase">Fan Passport</div>
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">Active · Follow, save, and register for events</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); openFanPassport(); }}
                                        className="pm-btn text-[10px] shrink-0"
                                        data-testid="account-open-fan-passport"
                                    >
                                        Open Fan Passport <ArrowRight className="w-3 h-3 ml-1 inline" />
                                    </button>
                                </li>
                            ) : (
                                <li className="border border-dashed border-[#2a2a2a] p-3" data-testid="account-fan-inactive">
                                    <div className="flex items-center gap-3">
                                        <Ticket className="w-4 h-4 text-[#666] shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-display font-bold uppercase text-[#a3a3a3]">Fan Passport</div>
                                            <div className="text-[10px] font-mono uppercase text-[#666]">Not activated · Follow, save, and register for events</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={doActivate}
                                            disabled={activating}
                                            className="pm-btn text-[10px] shrink-0"
                                            data-testid="account-activate-fan-passport"
                                        >
                                            {activating ? "Activating…" : "Activate Fan Passport"}
                                            {!activating && <ArrowRight className="w-3 h-3 ml-1 inline" />}
                                        </button>
                                    </div>
                                </li>
                            );
                        })()}
                        {artistList.map((a) => (
                            <li
                                key={`art-${a.id}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => openArtistWorkspace(a)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openArtistWorkspace(a); }}
                                className="group flex items-center gap-3 border border-[#2a2a2a] hover:border-[#ff4400] hover:bg-[#141414] p-3 cursor-pointer transition-colors"
                                data-testid={`account-relationship-artist-${a.id}`}
                            >
                                <Mic2 className="w-4 h-4 text-[#ff4400] shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-display font-bold uppercase truncate">{a.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">
                                        {ARTIST_ROLE_LABELS[a.role] || `Artist ${a.role || ""}`}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openArtistWorkspace(a); }}
                                    className="pm-btn pm-btn-primary text-[10px] shrink-0"
                                    data-testid={`account-open-artist-${a.id}`}
                                >
                                    Open Artist workspace <ArrowRight className="w-3 h-3 ml-1 inline" />
                                </button>
                            </li>
                        ))}                        {venueList.map((v) => (
                            <li
                                key={`ven-${v.id}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => openVenueWorkspace(v)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openVenueWorkspace(v); }}
                                className="group flex items-center gap-3 border border-[#2a2a2a] hover:border-[#ff4400] hover:bg-[#141414] p-3 cursor-pointer transition-colors"
                                data-testid={`account-relationship-venue-${v.id}`}
                            >
                                <Building2 className="w-4 h-4 text-[#ff4400] shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-display font-bold uppercase truncate">{v.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">
                                        {VENUE_ROLE_LABELS[v.role] || `Venue ${v.role || ""}`}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openVenueWorkspace(v); }}
                                    className="pm-btn pm-btn-primary text-[10px] shrink-0"
                                    data-testid={`account-open-venue-${v.id}`}
                                >
                                    Open Venue workspace <ArrowRight className="w-3 h-3 ml-1 inline" />
                                </button>
                            </li>
                        ))}
                        {promoterList.map((p) => (
                            <li
                                key={`prm-${p.id}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => nav(`/promoter/${p.id}/overview`)}
                                className="group flex items-center gap-3 border border-[#2a2a2a] hover:border-[#ff4400] hover:bg-[#141414] p-3 cursor-pointer transition-colors"
                                data-testid={`account-relationship-promoter-${p.id}`}
                            >
                                <Megaphone className="w-4 h-4 text-[#ff4400] shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-display font-bold uppercase truncate">{p.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">Promoter {p.role || "Team"}</div>
                                </div>
                                <button type="button" onClick={(e) => { e.stopPropagation(); nav(`/promoter/${p.id}/overview`); }} className="pm-btn pm-btn-primary text-[10px] shrink-0" data-testid={`account-open-promoter-${p.id}`}>
                                    Open Promoter workspace <ArrowRight className="w-3 h-3 ml-1 inline" />
                                </button>
                            </li>
                        ))}
                        {labelList.map((l) => (
                            <li
                                key={`lbl-${l.id}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => nav(`/label/${l.id}/overview`)}
                                className="group flex items-center gap-3 border border-[#2a2a2a] hover:border-[#ff4400] hover:bg-[#141414] p-3 cursor-pointer transition-colors"
                                data-testid={`account-relationship-label-${l.id}`}
                            >
                                <Disc3 className="w-4 h-4 text-[#ff4400] shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-display font-bold uppercase truncate">{l.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">Label {l.role || "Team"}</div>
                                </div>
                                <button type="button" onClick={(e) => { e.stopPropagation(); nav(`/label/${l.id}/overview`); }} className="pm-btn pm-btn-primary text-[10px] shrink-0" data-testid={`account-open-label-${l.id}`}>
                                    Open Label workspace <ArrowRight className="w-3 h-3 ml-1 inline" />
                                </button>
                            </li>
                        ))}
                    </ul>
                    {artistList.length === 0 && venueList.length === 0 && promoterList.length === 0 && labelList.length === 0 && (
                        <div className="mt-2 text-[10px] font-mono uppercase text-[#666]" data-testid="account-relationships-empty-hint">
                            One universal account — open an Artist or Venue workspace anytime from{" "}
                            <a href="/onboarding/artist" className="text-[#ff4400] hover:underline">Artist Setup</a>.
                        </div>
                    )}
                </div>
            </section>

            <section className="mt-6 border border-[#2a2a2a] p-6">
                <h2 className="font-display font-black uppercase text-lg">Profile</h2>
                <form onSubmit={saveProfile} className="mt-4 space-y-4">
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase text-[#a3a3a3]">Name</span>
                        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none" data-testid="account-name-input" />
                    </label>
                    <button type="submit" disabled={busy} className="pm-btn pm-btn-primary" data-testid="account-save-btn">
                        <Save className="w-4 h-4 mr-2" /> Save profile
                    </button>
                </form>
            </section>

            {user?.provider === "password" && (
                <section className="mt-6 border border-[#2a2a2a] p-6">
                    <h2 className="font-display font-black uppercase text-lg">Password</h2>
                    <form onSubmit={changePassword} className="mt-4 space-y-4">
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase text-[#a3a3a3]">Current password</span>
                            <input type="password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none" data-testid="account-current-password" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase text-[#a3a3a3]">New password (min 8)</span>
                            <input type="password" value={newPassword} onChange={(e) => setNew(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none" data-testid="account-new-password" />
                        </label>
                        <button type="submit" disabled={busy || newPassword.length < 8} className="pm-btn" data-testid="account-change-password-btn">Change password</button>
                    </form>
                </section>
            )}

            {error && <div className="mt-4 text-xs font-mono uppercase text-[#ff3333]" data-testid="account-error">{error}</div>}

            <div className="mt-8">
                <button onClick={doLogout} className="pm-btn" data-testid="account-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                </button>
            </div>

            <SelfDeleteAccountSection />
        </div>
    );
}
